import type { Row } from "./api";
import type { TaxTopic, TaxTopicBinding, TaxTopicId } from "./tax-topics";
import { parseFilingInterview } from "./filing-status";
import {
  filingVisibleFields,
  type FilingField,
} from "./filing-status-presentation";

export interface InterviewStage {
  id: string;
  label: string;
}

export interface InterviewPage {
  id: string;
  stageId: string;
  topicId: TaxTopicId;
  title: string;
  help: string;
  bindings: TaxTopicBinding[];
  filingFields?: FilingField[];
  itemIds?: string[];
  reviewOnly?: boolean;
  documents?: boolean;
}

/** The spouse stage is displayed only when a generated page belongs to it. */
export const interviewStages: readonly InterviewStage[] = [
  { id: "filing-status", label: "Filing Status" },
  { id: "spouse-information", label: "Spouse Information" },
  { id: "dependents", label: "Dependents" },
  { id: "income", label: "Income" },
  { id: "deductions", label: "Deductions" },
  { id: "credits", label: "Credits" },
  { id: "review", label: "Review" },
  { id: "federal-return", label: "Federal Return" },
];

type MasterPage = Omit<InterviewPage, "bindings"> & {
  questionId: string;
  itemIds: string[];
};

/** Explicit membership is presentation metadata, never a replacement source schema. */
export const interviewMasterPages: readonly MasterPage[] = [
  {
    id: "income-pay-and-tips",
    stageId: "income",
    topicId: "income",
    questionId: "Q-016",
    title: "Tips and overtime",
    help: "Review pay records for tips and overtime. Recording these amounts does not establish a deduction.",
    itemIds: ["WB-TIPS", "WB-OVERTIME"],
  },
  {
    id: "income-employer-benefits",
    stageId: "income",
    topicId: "income",
    questionId: "Q-016",
    title: "Employer benefits",
    help: "Check benefits and special payments received from employers or benefit providers.",
    itemIds: ["WB-SICK-PAY", "WB-EMPLOYER-EDUCATION", "WB-OTHER-FRINGE"],
  },
  {
    id: "income-special-compensation",
    stageId: "income",
    topicId: "income",
    questionId: "Q-016",
    title: "Other employment compensation",
    help: "Review employee classification, equity compensation, and foreign employment circumstances when they apply.",
    itemIds: ["WB-STATUTORY", "WB-EQUITY", "WB-FOREIGN-EXCLUSION"],
  },
  {
    id: "income-savings-and-investments",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Savings and investments",
    help: "Include interest, dividends, sales, and digital-asset transactions, even when a tax form has not arrived.",
    itemIds: ["OI-INTEREST", "OI-DIVIDEND", "OI-CAPITAL-DIGITAL"],
  },
  {
    id: "income-retirement-and-benefits",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Retirement income and benefits",
    help: "Review retirement distributions, pensions, Social Security, and Railroad Retirement statements.",
    itemIds: ["OI-RETIREMENT", "OI-SS-RR"],
  },
  {
    id: "income-work-and-property",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Business, property, and farm income",
    help: "Identify these income sources for follow-up. Capturing them here does not add support for preparing the related schedules.",
    itemIds: ["OI-BUSINESS", "OI-RENTAL-K1", "OI-FARM"],
  },
  {
    id: "income-other-payments-received",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Other payments received",
    help: "Review unemployment, refunds, alimony, winnings, prizes, and other receipts without assuming that every receipt is taxable.",
    itemIds: [
      "OI-UNEMPLOYMENT-ALIMONY-REFUND",
      "OI-GAMBLING-OTHER",
      "OI-GIFT-INHERIT-LIFE",
    ],
  },
  {
    id: "income-sales-debt-and-missing-forms",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Home sales, canceled debt, and missing forms",
    help: "Identify home sales, debt cancellation, and reportable income or transactions without a tax form.",
    itemIds: ["OI-HOME-SALE", "OI-COD", "OI-UNREPORTED"],
  },
  {
    id: "income-foreign-sources",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-017",
    title: "Income from outside the United States",
    help: "Review foreign income separately from citizenship, mailing address, and foreign-account ownership.",
    itemIds: ["OI-FOREIGN"],
  },
  {
    id: "income-international-and-territory",
    stageId: "income",
    topicId: "other-income",
    questionId: "Q-027",
    title: "International and U.S. territory information",
    help: "Check residence, accounts, assets, and territory matters. A domestic mailing address does not answer these questions.",
    itemIds: ["ITL-RESIDENCY", "ITL-ASSET", "ITL-TERRITORY"],
  },
  {
    id: "income-estimated-and-extension-payments",
    stageId: "income",
    topicId: "payments-withholding",
    questionId: "Q-024",
    title: "Estimated tax and extension payments",
    help: "Use payment confirmations to identify payments made separately from employer withholding.",
    itemIds: ["PY-ESTIMATED", "PY-EXTENSION"],
  },
  {
    id: "income-prior-year-refund",
    stageId: "income",
    topicId: "payments-withholding",
    questionId: "Q-024",
    title: "Prior-year refund applied to this year",
    help: "Check the prior-year return for an overpayment applied to 2025 rather than refunded.",
    itemIds: ["PY-PRIOR-APPLIED"],
  },
  {
    id: "income-other-payments-and-refundable-amounts",
    stageId: "income",
    topicId: "payments-withholding",
    questionId: "Q-024",
    title: "Other payments and refundable amounts",
    help: "Review other payments, excess withholding claims, and potential refundable credits. No extra payment sent does not mean no refundable credit.",
    itemIds: ["PY-OTHER"],
  },
  {
    id: "deductions-education-and-work",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-019",
    title: "Education and work expenses",
    help: "Review educator expenses and student-loan interest using the taxpayer’s records.",
    itemIds: ["AJ-EDUCATOR", "AJ-STUDENT-LOAN"],
  },
  {
    id: "deductions-health-and-retirement",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-019",
    title: "Health and retirement contributions",
    help: "Identify health-account activity, retirement contributions, and self-employed adjustments for review.",
    itemIds: ["AJ-HSA-MSA", "AJ-IRA", "AJ-SELF-EMPLOYED"],
  },
  {
    id: "deductions-other-adjustments",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-019",
    title: "Other adjustments to income",
    help: "Review other adjustments such as qualifying alimony paid, moving expenses, repayments, and withdrawal penalties.",
    itemIds: ["AJ-OTHER"],
  },
  {
    id: "deductions-additional-opportunities",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-020",
    title: "Additional deduction circumstances",
    help: "Identify tips, overtime, vehicle-loan interest, and age-related circumstances. The preparation review must still establish eligibility.",
    itemIds: ["S1A-TIPS", "S1A-OVERTIME", "S1A-VEHICLE", "S1A-SENIOR"],
  },
  {
    id: "deductions-home-and-taxes",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-025",
    title: "Home, interest, and state or local taxes",
    help: "Review tax payments and home-mortgage or investment interest before comparing deduction choices.",
    itemIds: ["IT-SALT", "IT-INTEREST"],
  },
  {
    id: "deductions-medical-expenses",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-025",
    title: "Medical and dental expenses",
    help: "Identify medical and dental expenses from the taxpayer’s records; an expense is not automatically deductible.",
    itemIds: ["IT-MEDICAL"],
  },
  {
    id: "deductions-charitable-giving",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-025",
    title: "Charitable giving and carryovers",
    help: "Review cash gifts, donated property, and unused charitable or other itemized deductions from earlier years.",
    itemIds: ["IT-CHARITY-CASH", "IT-CHARITY-NONCASH", "IT-CARRYOVER"],
  },
  {
    id: "deductions-losses-and-other-expenses",
    stageId: "deductions",
    topicId: "deductions",
    questionId: "Q-025",
    title: "Losses and other itemized expenses",
    help: "Review casualty or theft losses, repayments, gambling losses, and other special itemized expenses.",
    itemIds: ["IT-CASUALTY", "IT-OTHER"],
  },
  {
    id: "credits-family-and-care",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Children, care, and adoption",
    help: "Review family and care circumstances without assuming that the number of claimed dependents answers every credit question.",
    itemIds: ["CR-CTC-ODC", "CR-DEPENDENT-CARE", "CR-ADOPTION"],
  },
  {
    id: "credits-education",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Education expenses",
    help: "Review student and tuition records. Filing status and other eligibility limits require review before a credit can be claimed.",
    itemIds: ["CR-AOC", "CR-LLC"],
  },
  {
    id: "credits-work-and-retirement",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Work, retirement savings, and disability",
    help: "Review earned income, retirement saving, and age or disability circumstances. Having no dependents does not answer every item here.",
    itemIds: ["CR-EITC", "CR-SAVER", "CR-ELDERLY-DISABLED"],
  },
  {
    id: "credits-health-coverage",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Marketplace health coverage",
    help: "Check Marketplace coverage and Form 1095-A, including any advance payments made for the taxpayer’s coverage.",
    itemIds: ["CR-PTC"],
  },
  {
    id: "credits-home-and-vehicles",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Energy improvements and clean vehicles",
    help: "Identify relevant purchases and installation records for the tax year; purchase alone does not establish credit eligibility.",
    itemIds: ["CR-ENERGY", "CR-CLEAN-VEHICLE"],
  },
  {
    id: "credits-foreign-and-other",
    stageId: "credits",
    topicId: "credits",
    questionId: "Q-022",
    title: "Foreign and other credits",
    help: "Review foreign taxes, other possible credits, and credit carryforwards that may need specialized preparation.",
    itemIds: [
      "CR-FOREIGN",
      "CR-OTHER-NONREF",
      "CR-OTHER-REF",
      "CR-GENERAL-BUSINESS",
    ],
  },
  {
    id: "review-return-history",
    stageId: "review",
    topicId: "review",
    questionId: "Q-003",
    title: "Return history",
    help: "Confirm the kind of return and accounting period, then review earlier filings and special filing circumstances.",
    itemIds: [
      "RH-FILED",
      "RH-AMENDED",
      "RH-DECEDENT",
      "RH-BANKRUPTCY",
      "RH-RELIEF",
      "RH-REFUND-LIMIT",
    ],
  },
  {
    id: "review-prior-year-amounts",
    stageId: "review",
    topicId: "review",
    questionId: "Q-026",
    title: "Unused amounts from earlier years",
    help: "Review earlier returns for loss carryovers even if the taxpayer had no corresponding income this year.",
    itemIds: ["CE-NOL", "CE-CAPITAL", "CE-PASSIVE"],
  },
  {
    id: "review-elections-and-relief",
    stageId: "review",
    topicId: "review",
    questionId: "Q-026",
    title: "Prior choices and special relief",
    help: "Review tax elections and joint-liability or allocation relief. Current Single status does not erase earlier joint-return matters.",
    itemIds: ["CE-ELECTION", "CE-INJURED-INNOCENT"],
  },
  {
    id: "review-supporting-documents",
    stageId: "review",
    topicId: "review",
    questionId: "Q-028",
    title: "Other tax documents",
    help: "Compare the documents received with the income, deductions, and credits already discussed.",
    itemIds: ["XD-1099-1098", "XD-1095A-SSA-RRB-K1", "XD-OTHER"],
  },
  {
    id: "review-additional-deduction-checks",
    stageId: "review",
    topicId: "deductions",
    questionId: "Q-020",
    title: "Additional deduction review",
    help: "Eligibility and income-limit checks remain part of preparation review. Finishing the interview does not complete those checks.",
    itemIds: ["S1A-COMMON"],
    reviewOnly: true,
  },
  {
    id: "review-additional-tax-checks",
    stageId: "review",
    topicId: "review",
    questionId: "Q-023",
    title: "Additional tax review",
    help: "The preparation review must evaluate any additional taxes using the recorded income and circumstances. You are not being asked to calculate a tax liability here.",
    itemIds: [
      "OT-SELF-EMPLOYMENT",
      "OT-ADDITIONAL-MEDICARE",
      "OT-NIIT",
      "OT-HOUSEHOLD",
      "OT-RETIREMENT-HSA",
      "OT-RECAPTURE-SPECIAL",
    ],
    reviewOnly: true,
  },
];

/** Builds navigation only: never writes, clears a hidden answer, or establishes tax eligibility. */
export function buildInterviewPages(
  topics: TaxTopic[],
  selected: Row,
  valueFor: (card: TaxTopicBinding) => unknown,
): InterviewPage[] {
  const pages: InterviewPage[] = [];
  const find = (topicId: TaxTopicId, id: string) =>
    topics
      .find((topic) => topic.id === topicId)
      ?.blocks.flatMap((block) => block.bindings)
      .find((binding) => binding.record.id === id);
  const atomic = (
    id: string,
    stageId: string,
    topicId: TaxTopicId,
    sourceId: string,
    title: string,
    help: string,
    reviewOnly = false,
  ) => {
    const binding = find(topicId, sourceId);
    if (binding)
      pages.push({
        id,
        stageId,
        topicId,
        title,
        help,
        bindings: [binding],
        ...(reviewOnly ? { reviewOnly: true } : {}),
      });
  };
  const filing = find("filing-status", "Q-008");
  const raw = filing ? valueFor(filing) : selected.answers?.["Q-008"];
  const visible = filingVisibleFields(parseFilingInterview(raw), raw);
  const filingPage = (
    id: string,
    stageId: string,
    title: string,
    help: string,
    fields: FilingField[],
  ) => {
    const included = fields.filter((field) => visible.includes(field));
    if (filing && included.length)
      pages.push({
        id,
        stageId,
        topicId: "filing-status",
        title,
        help,
        bindings: [filing],
        filingFields: included,
      });
  };
  filingPage(
    "filing-status-choice",
    "filing-status",
    "Choose the filing status",
    "Select the intended filing status. The following pages collect the information needed to review that choice.",
    ["filingStatus"],
  );
  filingPage(
    "filing-status-relationship",
    "filing-status",
    "Relationship and filing circumstances",
    "Use the taxpayer’s relationship history and relevant records. Only circumstances relevant to the current answers are shown.",
    [
      "maritalSituation",
      "partnership",
      "spouseDeathYear",
      "recentSpouseDeath",
      "communityIncome",
    ],
  );
  atomic(
    "filing-status-tax-year",
    "filing-status",
    "personal-information",
    "Q-001",
    "Tax year",
    "Confirm the year of the federal return being prepared.",
  );
  atomic(
    "filing-status-taxpayer-name",
    "filing-status",
    "personal-information",
    "INT.taxpayer.name",
    "Taxpayer name",
    "Enter the taxpayer’s name as shown on their identity records.",
  );
  atomic(
    "filing-status-taxpayer-birth",
    "filing-status",
    "personal-information",
    "Q-010",
    "Date of birth and blindness",
    "Enter the date of birth. Age is calculated automatically and is not entered separately.",
  );
  atomic(
    "filing-status-taxpayer-address",
    "filing-status",
    "personal-information",
    "INT.taxpayer.mailing_address",
    "Mailing address",
    "Enter the address where the taxpayer receives mail. It does not by itself establish tax residence.",
  );
  atomic(
    "filing-status-taxpayer-contact",
    "filing-status",
    "personal-information",
    "INT.taxpayer.contact",
    "Contact information",
    "Enter the taxpayer’s contact details, or explicitly indicate when a detail is not provided.",
  );
  atomic(
    "filing-status-taxpayer-occupation",
    "filing-status",
    "personal-information",
    "INT.taxpayer.occupation",
    "Occupation",
    "Enter the taxpayer’s occupation for this return.",
  );
  atomic(
    "filing-status-taxpayer-residence",
    "filing-status",
    "personal-information",
    "Q-007",
    "Citizenship and residence",
    "Review citizenship and where the taxpayer lived during the tax year; do not infer either from the mailing address.",
  );
  atomic(
    "filing-status-identity-review",
    "filing-status",
    "personal-information",
    "Q-006",
    "Identity verification",
    "Identity-document verification requires its connected review workflow. Entering personal details does not complete verification.",
    true,
  );
  filingPage(
    "spouse-information-identity",
    "spouse-information",
    "Spouse information",
    "Enter the spouse’s identity details only when this filing-status workflow requires them.",
    ["spouseName", "spouseIdStatus", "spouseTaxId"],
  );
  filingPage(
    "spouse-information-tax-circumstances",
    "spouse-information",
    "Spouse tax circumstances",
    "Review only the residency, consent, deduction, or living-arrangement details relevant to the selected filing status.",
    [
      "spouseResidency",
      "spouseResidentElection",
      "jointReturnConsent",
      "spouseItemizes",
      "livedWithSpouse",
      "spouseAbsentLastSixMonths",
      "separateReturn",
    ],
  );
  atomic(
    "dependents-household-overview",
    "dependents",
    "dependents",
    "Q-009",
    "Dependents and household",
    "Confirm whether someone else can claim the taxpayer and identify dependents or qualifying people. Missing information is not the same as none.",
  );
  filingPage(
    "dependents-home-costs",
    "dependents",
    "Home and household costs",
    "Review the cost of maintaining the relevant home and any required earlier-year filing circumstances.",
    ["jointReturnEligibleInDeathYear", "paidMoreThanHalfHomeCosts"],
  );
  filingPage(
    "dependents-qualifying-person",
    "dependents",
    "Person supporting the filing status",
    "Use the person’s actual relationship, residence, and dependency circumstances. Retain any exceptions for review.",
    [
      "qualifyingPersonName",
      "qualifyingPersonRelationship",
      "qualifyingPersonResidence",
      "qualifyingPersonClaim",
    ],
  );
  atomic(
    "income-wage-statement-count",
    "income",
    "w2-information",
    "w2.expected_count",
    "Expected W-2 statements",
    "Enter how many W-2 statements are expected, including missing or corrected statements. Zero statements does not mean zero income.",
  );
  const countCard = find("w2-information", "w2.expected_count");
  const countRaw = countCard
    ? valueFor(countCard)
    : selected.facts?.["w2.expected_count"];
  const count =
    countRaw &&
    typeof countRaw === "object" &&
    (countRaw as Row).state === "KNOWN"
      ? (countRaw as Row).value
      : countRaw;
  if (typeof count === "number" && Number.isSafeInteger(count) && count > 0) {
    const bindings = [
      find("w2-information", "Q-011"),
      find("w2-information", "Q-012"),
    ].filter((binding): binding is TaxTopicBinding => Boolean(binding));
    if (bindings.length)
      pages.push({
        id: "income-wage-statement-documents",
        stageId: "income",
        topicId: "w2-information",
        title: "W-2 documents",
        help: "Organize the expected wage statements. Detailed document capture requires the connected wage-statement workflow.",
        bindings,
        documents: true,
      });
  }
  for (const definition of interviewMasterPages) {
    const binding = find(definition.topicId, definition.questionId);
    if (!binding) continue;
    const { questionId: _questionId, ...page } = definition;
    pages.push({
      ...page,
      itemIds: [...definition.itemIds],
      bindings: [binding],
    });
  }
  atomic(
    "review-special-circumstances",
    "review",
    "review",
    "Q-004",
    "Special filing circumstances",
    "Review a taxpayer’s death, bankruptcy, or special relief without assuming these circumstances from the selected filing status.",
  );
  pages.push({
    id: "review-overview",
    stageId: "review",
    topicId: "review",
    title: "Review the interview",
    help: "Review unanswered questions, conflicting information, and documents requiring follow-up. Completed interview pages do not mean the return is approved.",
    bindings: [],
    reviewOnly: true,
  });
  atomic(
    "federal-return-preferences",
    "federal-return",
    "federal-return",
    "INT.return.designations",
    "Return preferences",
    "Record the taxpayer’s preferences and whether another person may discuss this return with the IRS.",
  );
  pages.push({
    id: "federal-return-preparation",
    stageId: "federal-return",
    topicId: "federal-return",
    title: "Federal return preparation",
    help: "Review preparation availability separately from interview completion. This interview does not calculate tax, generate a completed return, or file with the IRS.",
    bindings: [],
    reviewOnly: true,
  });
  const stageOrder = new Map(
    interviewStages.map((stage, index) => [stage.id, index]),
  );
  return pages.sort(
    (a, b) =>
      (stageOrder.get(a.stageId) ?? Infinity) -
      (stageOrder.get(b.stageId) ?? Infinity),
  );
}
